import React, { useState, useCallback } from 'react';
import { SocialIcon } from 'react-social-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RemoveButton from './RemoveButton';

// Composant pour les éléments draggables
const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    backgroundColor: isDragging ? 'var(--color-background-hover, #f8f9fa)' : 'transparent',
    borderRadius: isDragging ? '8px' : '0',
    boxShadow: isDragging ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none',
    border: isDragging ? '2px solid var(--color-primary, #FF6B35)' : '2px solid transparent',
    padding: isDragging ? '4px' : '0',
    margin: isDragging ? '2px 0' : '0',
  };

  const dragHandleStyle = {
    cursor: isDragging ? 'grabbing' : 'grab',
    padding: '8px',
    marginRight: '8px',
    color: isDragging ? 'var(--color-primary, #FF6B35)' : '#666',
    fontSize: '16px',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    backgroundColor: isDragging ? 'rgba(255, 107, 53, 0.1)' : 'transparent',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div
          {...listeners}
          style={dragHandleStyle}
          title="Glisser pour réorganiser"
          onMouseEnter={(e) => {
            if (!isDragging) {
              e.target.style.backgroundColor = 'rgba(255, 107, 53, 0.05)';
              e.target.style.color = 'var(--color-primary, #FF6B35)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isDragging) {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#666';
            }
          }}
        >
          ⋮⋮
        </div>
        <div style={{ flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const SocialMedia = ({ formData, updateFormData }) => {
  const MAX_SOCIALS = 4;
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');


  const [socialOrder, setSocialOrder] = useState([]);
  
  // Autres réseaux dynamiques
  const [otherNetworks, setOtherNetworks] = useState(formData.otherNetworks || []);

  // Synchroniser otherNetworks avec formData.otherNetworks
  React.useEffect(() => {
    if (formData.otherNetworks && JSON.stringify(formData.otherNetworks) !== JSON.stringify(otherNetworks)) {
      setOtherNetworks(formData.otherNetworks);
    }
  }, [formData.otherNetworks, otherNetworks]);

  // Réseaux sociaux prédéfinis avec useMemo pour éviter les re-rendus
  const predefinedNetworks = React.useMemo(() => [
    { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/votre-profil', url: 'https://linkedin.com' },
    { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/votre-profil', url: 'https://facebook.com' },
    { key: 'twitter', label: 'X (Twitter)', placeholder: 'https://x.com/votre-profil', url: 'https://x.com' },
    { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/votre-profil', url: 'https://instagram.com' },
    { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/votre-profil', url: 'https://youtube.com' },
    { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@votre-profil', url: 'https://tiktok.com' }
  ], []);

  // Configuration des sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fonction pour gérer la fin du drag
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = socialOrder.findIndex(item => item.id === active.id);
      const newIndex = socialOrder.findIndex(item => item.id === over.id);
      
      const newOrder = arrayMove(socialOrder, oldIndex, newIndex);
      setSocialOrder(newOrder);
      
      // Mettre à jour formData avec le nouvel ordre
      updateFormData('socialOrder', newOrder);
    }
  };

  const initializeSocialOrder = useCallback(() => {
    const allNetworks = [];
    
    // Ajouter les réseaux prédéfinis qui ont une URL (exclure null, undefined et chaînes vides)
    predefinedNetworks.forEach((network, index) => {
      const socialValue = formData[`social_${network.key}`];
      if (socialValue && socialValue.trim()) {
        allNetworks.push({
          id: `predefined_${network.key}`,
          type: 'predefined',
          key: network.key,
          data: network,
          order: index
        });
      }
    });
    
    // Ajouter TOUS les autres réseaux (même sans URL pour qu'ils apparaissent dans la liste)
    otherNetworks.forEach((network, index) => {
      allNetworks.push({
        id: `other_${network.id}`,
        type: 'other',
        key: network.id,
        data: network,
        order: predefinedNetworks.length + index
      });
    });
    
    // Trier par ordre existant ou utiliser l'ordre par défaut
    const existingOrder = formData.socialOrder || [];
    if (existingOrder.length > 0) {
      allNetworks.sort((a, b) => {
        const aIndex = existingOrder.findIndex(item => item.id === a.id);
        const bIndex = existingOrder.findIndex(item => item.id === b.id);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }
    
    setSocialOrder(allNetworks);
  }, [formData, otherNetworks, predefinedNetworks]);

  // Initialiser les réseaux par défaut une seule fois au montage initial
  const [hasInitialized, setHasInitialized] = React.useState(false);
  
  React.useEffect(() => {
    if (!hasInitialized) {
      const defaultNetworks = ['linkedin', 'facebook', 'instagram'];
      const hasAnyDefaultNetwork = defaultNetworks.some(key => formData[`social_${key}`] !== undefined);
      const hasAnySocialData = Object.keys(formData).some(key => key.startsWith('social_'));
      
      // Seulement ajouter les réseaux par défaut si c'est le premier chargement (aucune donnée sociale)
      if (!hasAnyDefaultNetwork && !hasAnySocialData) {
        defaultNetworks.forEach(key => {
          updateFormData(`social_${key}`, '');
        });
      }
      setHasInitialized(true);
    }
  }, [formData, updateFormData, hasInitialized]);

  // Initialiser l'ordre quand les données changent
  React.useEffect(() => {
    initializeSocialOrder();
  }, [otherNetworks, initializeSocialOrder]);

  // Mettre à jour l'ordre quand les réseaux sociaux changent
  React.useEffect(() => {
    const socialKeys = Object.keys(formData).filter(key => key.startsWith('social_') && key !== 'socialOrder');
    if (socialKeys.length > 0) {
      initializeSocialOrder();
    }
  }, [formData, initializeSocialOrder]);

  // État dérivé pour savoir si la limite est atteinte
  const isMaxReached = (socialOrder?.length || 0) >= MAX_SOCIALS;





  // Galerie d'icônes pour les autres réseaux - Seulement les réseaux supportés par react-social-icons
  const iconGallery = [
    // Réseaux sociaux principaux
    { id: 'linkedin', name: 'LinkedIn', url: 'https://linkedin.com' },
    { id: 'facebook', name: 'Facebook', url: 'https://facebook.com' },
    { id: 'instagram', name: 'Instagram', url: 'https://instagram.com' },
    { id: 'twitter', name: 'X (Twitter)', url: 'https://x.com' },
    { id: 'tiktok', name: 'TikTok', url: 'https://tiktok.com' },
    { id: 'youtube', name: 'YouTube', url: 'https://youtube.com' },
    // Autres réseaux supportés
    { id: 'twitch', name: 'Twitch', url: 'https://twitch.tv' },
    { id: 'github', name: 'GitHub', url: 'https://github.com' },

    { id: 'pinterest', name: 'Pinterest', url: 'https://pinterest.com' },
    { id: 'whatsapp', name: 'WhatsApp', url: 'https://whatsapp.com' },
    { id: 'telegram', name: 'Telegram', url: 'https://telegram.org' },
    { id: 'reddit', name: 'Reddit', url: 'https://reddit.com' },

    { id: 'vimeo', name: 'Vimeo', url: 'https://vimeo.com' },
    { id: 'spotify', name: 'Spotify', url: 'https://spotify.com' },


    { id: 'discord', name: 'Discord', url: 'https://discord.com' },
    { id: 'slack', name: 'Slack', url: 'https://slack.com' },

  ];



  const handleSocialChange = (network, value) => {
    updateFormData(`social_${network}`, value);
  };



  // Ajouter un réseau depuis la galerie
  const addNetworkFromGallery = (networkData) => {
    // Bloquer l'ajout si la limite est atteinte
    if ((socialOrder?.length || 0) >= MAX_SOCIALS) {
      return;
    }
    // Vérifier si c'est un réseau prédéfini
    const predefinedNetwork = predefinedNetworks.find(network => network.key === networkData.id);
    
    if (predefinedNetwork) {
      // Ajouter comme réseau prédéfini avec l'URL par défaut
      updateFormData(`social_${predefinedNetwork.key}`, predefinedNetwork.url);
      
      // Ajouter immédiatement le réseau à socialOrder
      const newOrderItem = {
        id: `predefined_${predefinedNetwork.key}`,
        type: 'predefined',
        key: predefinedNetwork.key,
        data: predefinedNetwork,
        order: socialOrder.length
      };
      const updatedOrder = [...socialOrder, newOrderItem];
      setSocialOrder(updatedOrder);
      updateFormData('socialOrder', updatedOrder);
    } else {
      // Ajouter comme autre réseau
      const newNetwork = {
        id: Date.now(),
        name: networkData.name,
        url: '',
        networkUrl: networkData.url
      };
      const updatedNetworks = [...otherNetworks, newNetwork];
      setOtherNetworks(updatedNetworks);
      updateFormData('otherNetworks', updatedNetworks);
      
      // Ajouter immédiatement le réseau à socialOrder pour qu'il apparaisse dans la liste
      const newOrderItem = {
        id: `other_${newNetwork.id}`,
        type: 'other',
        key: newNetwork.id,
        data: newNetwork,
        order: socialOrder.length
      };
      const updatedOrder = [...socialOrder, newOrderItem];
      setSocialOrder(updatedOrder);
      updateFormData('socialOrder', updatedOrder);
    }
    
    setShowGalleryModal(false);
  };

  // Supprimer un autre réseau
  const removeOtherNetwork = (id) => {
    const updatedNetworks = otherNetworks.filter(network => network.id !== id);
    setOtherNetworks(updatedNetworks);
    updateFormData('otherNetworks', updatedNetworks);
  };

  // Mettre à jour un autre réseau
  const updateOtherNetwork = (id, field, value) => {
    const updatedNetworks = otherNetworks.map(network => 
      network.id === id ? { ...network, [field]: value } : network
    );
    setOtherNetworks(updatedNetworks);
    updateFormData('otherNetworks', updatedNetworks);
  };

  // Supprimer un réseau prédéfini
  const removePredefinedNetwork = (key) => {
    // Supprimer complètement les champs du réseau prédéfini du formData
    const newFormData = { ...formData };
    delete newFormData[`social_${key}`];
    delete newFormData[`social_${key}_style`];
    
    // Mettre à jour le formData avec les champs supprimés
    Object.keys(newFormData).forEach(field => {
      updateFormData(field, newFormData[field]);
    });
    
    // Supprimer explicitement les champs
    updateFormData(`social_${key}`, undefined);
    updateFormData(`social_${key}_style`, undefined);
    
    // Mettre à jour socialOrder pour supprimer le réseau
    const updatedOrder = socialOrder.filter(item => !(item.type === 'predefined' && item.key === key));
    setSocialOrder(updatedOrder);
    updateFormData('socialOrder', updatedOrder);
  };

  // Filtrer les icônes de la galerie
  const filteredIcons = iconGallery.filter(icon => {
    // Vérifier si le réseau correspond au terme de recherche
    const matchesSearch = icon.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Vérifier si le réseau n'est pas déjà ajouté
    const isAlreadyAdded = socialOrder.some(item => {
      if (item.type === 'predefined') {
        return item.key === icon.id;
      } else if (item.type === 'other') {
        return item.data.name === icon.name;
      }
      return false;
    });
    
    return matchesSearch && !isAlreadyAdded;
  });

  return (
    <div className="form-section-content">
      <h3 className="section-title">Réseaux sociaux</h3>
      
      {/* Section Icônes ajoutées */}
      <div style={{ marginBottom: '30px' }}>
        {socialOrder.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={socialOrder.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {socialOrder.map((item) => {
                const network = item.data;
                const isOther = item.type === 'other';
                
                return (
                  <SortableItem key={item.id} id={item.id}>
                    <div className="form-group">
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                        <label className="form-label" style={{ flex: 1, margin: 0 }}>
                          {isOther ? network.name : network.label}
                        </label>

                        <RemoveButton
                          onClick={() => isOther ? removeOtherNetwork(network.id) : removePredefinedNetwork(network.key)}
                        />
                       </div>
                       <input
                         type="url"
                         className="form-input"
                         placeholder={isOther ? "https://exemple.com/votre-profil" : network.placeholder}
                         value={isOther ? network.url : (formData[`social_${network.key}`] || '')}
                         onChange={(e) => {
                           if (isOther) {
                             updateOtherNetwork(network.id, 'url', e.target.value);
                           } else {
                             handleSocialChange(network.key, e.target.value);
                           }
                         }}
                       />
                     </div>
                   </SortableItem>
                 );
               })}
             </SortableContext>
           </DndContext>
         ) : (
           <p style={{ color: 'var(--color-text-light)', fontStyle: 'italic' }}>
             Aucun réseau social ajouté. Utilisez le bouton ci-dessous pour en ajouter.
           </p>
         )}

        <button
          type="button"
          onClick={() => { if (!isMaxReached) setShowGalleryModal(true); }}
          disabled={isMaxReached}
          style={{
            background: isMaxReached ? 'var(--color-border)' : 'var(--color-primary)',
            color: isMaxReached ? 'var(--color-text-light)' : 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 15px',
            cursor: isMaxReached ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            marginTop: '25px',
            opacity: isMaxReached ? 0.7 : 1,
            transition: 'opacity 0.2s ease'
          }}
        >
          {isMaxReached ? 'Limite de 4 réseaux atteinte' : '+ Ajouter un autre réseau'}
        </button>
        {isMaxReached && (
          <p style={{ color: 'var(--color-text-light)', fontSize: '12px', marginTop: '8px' }}>
            Supprimez un réseau pour en ajouter un nouveau (maximum 4).
          </p>
        )}
      </div>

      {/* Modal Galerie */}
      {showGalleryModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowGalleryModal(false);
            }
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              position: 'relative',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: 'var(--color-text)' }}>
                Choisir un réseau social
              </h4>
              <button
                type="button"
                onClick={() => setShowGalleryModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--color-text)'
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Recherche */}
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Rechercher une icône..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Grille d'icônes */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: '20px'
            }}>
              {filteredIcons.map((icon) => (
                <div
                  key={icon.id}
                  onClick={() => addNetworkFromGallery(icon)}
                  style={{
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    padding: '15px',
                    textAlign: 'center',
                    cursor: isMaxReached ? 'not-allowed' : 'pointer',
                    background: 'var(--color-background)',
                    transition: 'all 0.2s ease',
                    opacity: isMaxReached ? 0.6 : 1,
                    pointerEvents: isMaxReached ? 'none' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <SocialIcon url={icon.url} style={{ height: 48, width: 48, margin: '0 auto 12px', pointerEvents: 'none' }} target="_self" />
                  <p style={{ fontSize: '13px', margin: 0, fontWeight: '500', color: 'var(--color-text)' }}>{icon.name}</p>
                </div>
              ))}
            </div>

            {filteredIcons.length === 0 && searchTerm && (
              <div style={{
                textAlign: 'center',
                padding: '30px',
                color: 'var(--color-text-light)',
                fontSize: '14px'
              }}>
                Aucune icône trouvée pour "<strong>{searchTerm}</strong>"
              </div>
            )}
          </div>
        </div>
      )}


    </div>
  );
};

export default SocialMedia;
